import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { Marker, DrawingManager, GoogleMap } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer as DeckHeatmapLayer } from '@deck.gl/aggregation-layers';
import {
  SHAPE_TYPE,
  NEIGHBORHOOD_STYLE,
  DISTRICT_STYLE,
  CITY_STYLE,
  MAP_DEFAULT_VALUES,
} from 'helpers/constants';
import { useSelector } from 'react-redux';
import {
  getOrderCountOfZone,
  setDrawnPolygonPaths,
  setPathsOfPolygon,
  setRivalsByLocationLatAndLngValue,
} from '../../../store/requests/zone';
import { getOrdersLocations } from 'store/requests/order';
import { debounce } from 'lodash';
import { wktToGeoJSON } from '@terraformer/wkt';
import ViewZonePolygons from '../../../components/Zones/PolygonComponents/ViewZonePolygons';
import ViewSelectedPolygon from '../../../components/Zones/PolygonComponents/ViewSelectedPolygon';
import ViewSelectedRowsFromTable from '../../../components/Zones/PolygonComponents/ViewSelectedRowsFromTable';
import ViewSelectedRowsForVersionHistoryTable from '../../../components/Zones/PolygonComponents/ViewSelectedRowsForVersionHistoryTable';
import ViewNewDrawnPolygon from '../../../components/Zones/PolygonComponents/ViewNewDrawnPolygon';
import ViewSelectedZoneForEdit from '../../../components/Zones/PolygonComponents/ViewSelectedZoneForEdit';
import ViewSelectedVersionZoneForEdit from '../../../components/Zones/PolygonComponents/ViewSelectedVersionZoneForEdit';
import ViewStoreMarkers from '../../../components/Zones/MarkerComponents/ViewStoreMarkers';
import ViewRivalsByLocationWithClusterer from '../../../components/Zones/MarkerComponents/ViewRivalsByLocationWithClusterer';
import ViewNearbyZonesPolygon from '../../../components/Zones/PolygonComponents/ViewNearbyZonesPolygon';
import { getZoneOptions } from 'helpers/utils';
import moment from 'moment';
import { setSelectedVersionRows } from '../../../store/requests/zone';

const MapOperations = (props) => {
  const {
    zonesAllInfo,
    refreshMap,
    selectedRowsForVersionHistoryTable,
    selectedCityDistrictOrNeighborhoodConfig,
    customerLocation,
    setSelectedRowKeys,
    selectedRowKeys,
  } = props;

  const [selectedPolygon, setSelectedPolygon] = useState([]);
  const [boundariesOfMap, setBoundariesOfMap] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const map = useRef();
  const polygonRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const heatmapOverlayRef = useRef(null);
  const containerStyle = {
    width: '100%',
    height: '81vh',
  };
  const zoneState = useSelector((state) => state.zone.geoRadarZone) ?? null;

  const googleMapInstance = map.current;

  const cityData = useMemo(
    () => window.google && new window.google.maps.Data(),
    [googleMapInstance],
  );
  const districtData = useMemo(
    () => window.google && new window.google.maps.Data(),
    [googleMapInstance],
  );
  const neighborhoodData = useMemo(
    () => window.google && new window.google.maps.Data(),
    [googleMapInstance],
  );

  useEffect(() => {
    const filteredArray = selectedPolygon?.filter((obj) =>
      selectedRowKeys.includes(obj.id),
    );
    setSelectedPolygon(filteredArray);
  }, [selectedRowKeys]);

  const options = getZoneOptions({
    fillOpacity: zoneState?.zoneOpacity,
    fillColor: zoneState?.zoneColor?.color,
  });

  const clearMapData = (data) => {
    if (data) {
      data.setMap(null);
      data.forEach((feature) => {
        data.remove(feature);
      });
    }
  };

  useEffect(() => {
    clearMapData(cityData);
    clearMapData(districtData);
    clearMapData(neighborhoodData);

    cityData?.setMap(googleMapInstance);
    districtData?.setMap(googleMapInstance);
    neighborhoodData?.setMap(googleMapInstance);

    addData(
      cityData,
      selectedCityDistrictOrNeighborhoodConfig.cityBoundaries,
      CITY_STYLE,
    );
    addData(
      districtData,
      selectedCityDistrictOrNeighborhoodConfig.districtBoundaries,
      DISTRICT_STYLE,
    );
    addBoundaryDataByRegion(
      neighborhoodData,
      selectedCityDistrictOrNeighborhoodConfig.neighborHoodBoundaries,
      NEIGHBORHOOD_STYLE,
    );
  }, [selectedCityDistrictOrNeighborhoodConfig]);

  useEffect(() => {
    async function loadData() {
      if (zoneState?.heatmapToggle) {
        let locations = [];
        const orderLocationsInfo = {
          northEastLocation: {
            latitude: boundariesOfMap?.ne?.lat(),
            longitude: boundariesOfMap?.ne?.lng(),
          },
          southWestLocation: {
            latitude: boundariesOfMap?.sw?.lat(),
            longitude: boundariesOfMap?.sw?.lng(),
          },
          startDate: zoneState?.heatmapDateRange?.startDate,
          endDate: zoneState?.heatmapDateRange?.finishDate,
        };

        const responseOrderLocations =
          await getOrdersLocations(orderLocationsInfo);
        for (var i = 0; i < responseOrderLocations?.data.length; i++) {
          locations.push({
            latitude: responseOrderLocations?.data[i]?.latitude,
            longitude: responseOrderLocations?.data[i]?.longitude,
            weight: responseOrderLocations?.data[i]?.weight,
          });
        }
        setHeatmapData(locations);
      }
    }
    loadData();
  }, [zoneState?.heatmapDateRange, boundariesOfMap]);

  useEffect(() => {
    if (!map.current) return;

    if (
      zoneState?.heatmapToggle &&
      zoneState?.heatmapDateRange &&
      heatmapData &&
      heatmapData.length > 0
    ) {
      const heatmapLayer = new DeckHeatmapLayer({
        id: 'zone-heatmap',
        data: heatmapData,
        getPosition: (d) => [d.longitude, d.latitude],
        getWeight: (d) => d.weight || 1,
        radiusPixels: 30,
        intensity: 1,
        threshold: 0.05,
        aggregation: 'SUM',
      });

      if (!heatmapOverlayRef.current) {
        heatmapOverlayRef.current = new GoogleMapsOverlay({
          layers: [heatmapLayer],
        });
        heatmapOverlayRef.current.setMap(map.current);
      } else {
        heatmapOverlayRef.current.setProps({ layers: [heatmapLayer] });
      }
    } else {
      if (heatmapOverlayRef.current) {
        heatmapOverlayRef.current.setProps({ layers: [] });
      }
    }
  }, [heatmapData, zoneState?.heatmapToggle, zoneState?.heatmapDateRange]);

  useEffect(() => {
    return () => {
      if (heatmapOverlayRef.current) {
        heatmapOverlayRef.current.finalize();
        heatmapOverlayRef.current = null;
      }
    };
  }, []);

  const addData = (data, boundaries, style) => {
    const geoJsonData = {
      type: 'FeatureCollection',
      features: boundaries?.map((boundary) => ({
        type: 'Feature',
        geometry: JSON.parse(boundary.polygonGeoJSON) || null,
      })),
    };

    if (
      data &&
      typeof data.addGeoJson === 'function' &&
      boundaries?.length > 0
    ) {
      data.addGeoJson(geoJsonData);
      data.setStyle(style);
    }
  };

  const addBoundaryDataByRegion = (data, boundaries, style) => {
    const geoJsonData = {
      type: 'FeatureCollection',
      features: boundaries?.map((boundary) => ({
        type: 'Feature',
        geometry: wktToGeoJSON(boundary.polygonGeoJSON),
      })),
    };

    if (
      data &&
      typeof data.addGeoJson === 'function' &&
      boundaries?.length > 0
    ) {
      data.addGeoJson(geoJsonData);
      data.setStyle(style);
    }
  };

  useEffect(() => {
    if (zoneState?.isAddOpen) {
      setDrawing(true);
    }
  }, [zoneState?.isAddOpen]);

  useEffect(() => {
    if (refreshMap) {
      setDrawing(false);
      setDrawnPolygonPaths(null);
    }
  }, [refreshMap]);

  useEffect(() => {
    if (map.current && customerLocation) {
      map.current.setCenter(customerLocation);
    }
  }, [customerLocation]);

  useEffect(() => {
    if (zoneState?.selectedVersionRows) {
      if (zoneState?.selectedVersionRows.length > 0) {
        getBounds(zoneState?.selectedVersionRows);
      } else {
        if (zonesAllInfo.length > 0) {
          getBounds(zonesAllInfo);
        }
      }
    }
  }, [zoneState?.selectedVersionRows]);

  const getBounds = (selectedRows) => {
    if (window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      if (selectedRows) {
        selectedRows.forEach((polygon) => {
          polygon.zonePolygonJson?.forEach((point) => {
            bounds.extend(point);
          });
        });
      }
      map.current?.fitBounds(bounds);
    }
  };

  useEffect(() => {
    if (
      selectedRowsForVersionHistoryTable &&
      selectedRowsForVersionHistoryTable.length > 0
    ) {
      getBounds(selectedRowsForVersionHistoryTable);
    }
  }, [selectedRowsForVersionHistoryTable]);

  useEffect(() => {
    if (zonesAllInfo.length > 0) {
      const allNullPolygon = zonesAllInfo.every(
        (zoneInfo) => zoneInfo.zonePolygonJson === null,
      );

      if (allNullPolygon) {
        const firstStore = zonesAllInfo[0]?.store;
        if (firstStore) {
          map.current?.setCenter({
            lat: firstStore.latitude,
            lng: firstStore.longitude,
          });
          map.current?.setZoom(14);
        }
      } else {
        getBounds(zonesAllInfo);
      }
    }
  }, [zonesAllInfo]);

  const generateGeoJSONCircle = (center, radius, numSides) => {
    let points = [],
      degreeStep = 360 / numSides;

    for (let i = 0; i < numSides; i++) {
      let gpos = new window.google.maps.geometry.spherical.computeOffset(
        center,
        radius,
        degreeStep * i,
      );
      points.push([gpos.lng(), gpos.lat()]);
    }
    // Duplicate the last point to close the geojson ring
    points.push(points[0]);

    return {
      type: 'Polygon',
      coordinates: [points],
    };
  };

  const onShapesComplete = (shape, shapeType) => {
    setDrawnPolygonPaths(null);
    let latlngCoordinates = [];

    if (shapeType === SHAPE_TYPE.CIRCLE) {
      const radius = shape.getRadius();
      const center = shape.getCenter();

      const polygon = generateGeoJSONCircle(center, radius, 20);
      polygon.coordinates.forEach((latLon) => {
        latLon.forEach((ll) => {
          latlngCoordinates.push({
            lat: ll[1],
            lng: ll[0],
          });
        });
      });

      // Construct the polygon.
      const newPolygon = new window.google.maps.Polygon({
        paths: latlngCoordinates,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: zoneState?.zoneColor.color,
        fillOpacity: zoneState?.zoneOpacity,
      });
      setPolygonPaths(newPolygon);
      shape.setMap(null);
    } else if (shapeType === SHAPE_TYPE.RECTANGLE) {
      const rectangleBounds = shape.getBounds();
      const polygonDots = [
        {
          lat: rectangleBounds.getNorthEast().lat(),
          lng: rectangleBounds.getNorthEast().lng(),
        },
        {
          lat: rectangleBounds.getNorthEast().lat(),
          lng: rectangleBounds.getSouthWest().lng(),
        },
        {
          lat: rectangleBounds.getSouthWest().lat(),
          lng: rectangleBounds.getSouthWest().lng(),
        },
        {
          lat: rectangleBounds.getSouthWest().lat(),
          lng: rectangleBounds.getNorthEast().lng(),
        },
      ];

      const polygon = new window.google.maps.Polygon({
        paths: polygonDots,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: zoneState?.zoneColor.color,
        fillOpacity: zoneState?.zoneOpacity,
      });

      setPolygonPaths(polygon);
      shape.setMap(null);
    } else {
      setPolygonPaths(shape);
    }
  };

  const setPolygonPaths = (polygon) => {
    let polygonDots = [];
    polygon
      .getPath()
      .getArray()
      .map((latLon) => {
        polygonDots.push({
          lat: latLon.lat(),
          lng: latLon.lng(),
        });
      });
    setDrawnPolygonPaths(polygonDots);
    setPathsOfPolygon(polygonDots);
    polygon.setMap(null);
    setDrawing(false);
  };

  const onPolygonClick = async (clickEvent, zone) => {
    const time = {
      endDate: moment().unix(),
      startDate: moment().subtract(1, 'month').unix(),
    };

    const { averageOrderCount } = await getOrderCountOfZone(time, zone.id);

    const latLng = {
      lat: clickEvent.latLng.lat(),
      lng: clickEvent.latLng.lng(),
    };
    setSelectedPolygon((prevArray) => [
      ...prevArray,
      { ...zone, ...latLng, averageOrderCount },
    ]);
    setSelectedRowKeys((prevArray) => [...prevArray, zone.id]);
  };

  const defaultMapPosition = (zoom = MAP_DEFAULT_VALUES.ZOOM) => {
    if (!map.current) {
      return;
    }

    map.current.setZoom(zoom);
    map.current.setCenter({
      lat: MAP_DEFAULT_VALUES.LAT,
      lng: MAP_DEFAULT_VALUES.LNG,
    });
  };

  const onMapLoaded = (loadedMap) => {
    map.current = loadedMap;

    defaultMapPosition();
  };

  const onCloseInfoWindow = (polygon) => {
    setSelectedPolygon((prevArray) =>
      prevArray.filter((element) => element.id !== polygon.id),
    );
    setSelectedRowKeys((prevArray) =>
      prevArray.filter((element) => element !== polygon.id),
    );
    setSelectedVersionRows(
      zoneState.selectedVersionRows?.filter(
        (element) => element.id !== polygon.id,
      ),
    );
  };

  const onLoad = useCallback(
    (polygon) => {
      polygonRef.current = polygon;
    },
    [onPolygonClick],
  );

  const onBoundsChanged = useCallback(
    debounce(() => {
      const bounds = map.current.getBounds();
      const ne = bounds?.getNorthEast();
      const sw = bounds?.getSouthWest();

      setBoundariesOfMap({ ne, sw });

      const northEastAndSouthWestData = {
        northEastLocation: {
          lat: ne?.lat(),
          lng: ne?.lng(),
        },
        southWestLocation: {
          lat: sw?.lat(),
          lng: sw?.lng(),
        },
      };
      setRivalsByLocationLatAndLngValue(northEastAndSouthWestData);
    }, 500),
    [],
  );

  const onUnmount = useCallback(() => {
    polygonRef.current = null;
  }, []);

  useEffect(() => {
    if (
      zoneState?.selectedZoneItem &&
      zoneState?.selectedZoneItem?.zonePolygonJson?.length > 0
    ) {
      getBoundsForSelectedZoneItem(
        zoneState?.selectedZoneItem?.zonePolygonJson,
      );
    } else if (zoneState?.selectedZoneItem?.store) {
      map.current?.setCenter({
        lat: zoneState?.selectedZoneItem?.store.latitude,
        lng: zoneState?.selectedZoneItem?.store.longitude,
      });
      map.current?.setZoom(14);
    }
  }, [zoneState?.selectedZoneItem]);

  const getBoundsForSelectedZoneItem = (zonePolygonJson) => {
    if (window.google && window.google.maps && map.current) {
      const bounds = new window.google.maps.LatLngBounds();
      if (zonePolygonJson) {
        zonePolygonJson?.forEach((point) => {
          bounds.extend(point);
        });
      }
      map.current.fitBounds(bounds);
    }
  };

  useEffect(() => {
    if (
      zoneState?.selectedVersionZoneItem &&
      zoneState?.selectedVersionZoneItem?.zonePolygonJson?.length > 0
    ) {
      getBoundsForSelectedZoneItem(
        zoneState?.selectedVersionZoneItem?.zonePolygonJson,
      );
    }
  }, [zoneState?.selectedVersionZoneItem]);

  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onMapLoaded}
        onBoundsChanged={onBoundsChanged}
      >
        <ViewZonePolygons
          zonesAllInfo={zonesAllInfo}
          polygonRef={polygonRef}
          onPolygonClick={onPolygonClick}
          onLoad={onLoad}
          onUnmount={onUnmount}
        />
        <ViewNearbyZonesPolygon />
        <ViewStoreMarkers zonesAllInfo={zonesAllInfo} />
        <ViewRivalsByLocationWithClusterer />
        {selectedPolygon && (
          <ViewSelectedPolygon
            selectedPolygon={selectedPolygon}
            onCloseInfoWindow={onCloseInfoWindow}
          />
        )}
        {zoneState?.selectedVersionRows && <ViewSelectedRowsFromTable />}
        {selectedRowsForVersionHistoryTable?.length > 0 && (
          <ViewSelectedRowsForVersionHistoryTable
            selectedRowsForVersionHistoryTable={
              selectedRowsForVersionHistoryTable
            }
          />
        )}
        {zoneState?.isAddOpen && drawing && (
          <DrawingManager
            ref={drawingManagerRef}
            onPolygonComplete={(polygon) =>
              onShapesComplete(polygon, SHAPE_TYPE.POLYGON)
            }
            onCircleComplete={(circle) =>
              onShapesComplete(circle, SHAPE_TYPE.CIRCLE)
            }
            onRectangleComplete={(rectangle) =>
              onShapesComplete(rectangle, SHAPE_TYPE.RECTANGLE)
            }
            drawingMode={'polygon'}
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: window.google.maps.ControlPosition.TOP_RIGHT,
                drawingModes: [
                  window.google.maps.drawing.OverlayType.CIRCLE,
                  window.google.maps.drawing.OverlayType.POLYGON,
                  window.google.maps.drawing.OverlayType.RECTANGLE,
                ],
              },
              polygonOptions: options,
              circleOptions: options,
              rectangleOptions: options,
            }}
          />
        )}
        {zoneState?.drawnPolygonPaths?.length > 0 && (
          <ViewNewDrawnPolygon onShapesComplete={onShapesComplete} />
        )}
        {zoneState?.selectedZoneItem && (
          <ViewSelectedZoneForEdit
            polygonRef={polygonRef}
            onLoad={onLoad}
            onUnmount={onUnmount}
          />
        )}
        {zoneState?.selectedVersionZoneItem && (
          <ViewSelectedVersionZoneForEdit
            polygonRef={polygonRef}
            onLoad={onLoad}
            onUnmount={onUnmount}
          />
        )}
        {customerLocation && <Marker position={customerLocation} />}
      </GoogleMap>
    </>
  );
};

export default MapOperations;
